<template>
  <div class="config-editor">

    <v-divider class="mb-3" />

    <v-container fluid class="pa-0">
      <v-card>
        <div class="config-editor__tabs-sticky">
          <v-tabs v-model="innerTab" class="config-editor__tabs">
            <v-tab
              v-for="tab in $options.configEditorTabs"
              :key="tab.value"
              :tab-value="tab.value"
              @click="scrollToSection(tab.value)"
            >
              {{ tab.label }}
            </v-tab>
          </v-tabs>
        </div>

        <div class="px-4 pb-4">
          <div class="d-flex align-center mb-2">
            <div class="d-flex align-center">
              <div>
                <div class="caption grey--text ">Main Domain (Default Destination)</div>
                <div class="d-flex align-center mt-1" style="gap: 8px">
                  <span class="body-2 font-weight-medium">{{ mainDomain?.name || '' }}</span>
                </div>
              </div>
              <v-chip
                v-if="mainDomain?.status"
                small
                outlined
                :color="mainDomain.status === 'active' ? 'success' : 'grey'"
                class="ml-4 pa-2 text-capitalize"
              >
                {{ mainDomain.status }}
              </v-chip>
            </div>
            <v-spacer />
            <v-btn
              v-if="!readonly"
              outlined
              small color="warning"
              class="text-uppercase px-2"
              @click="maintenanceDialog = true"
            >
              Maintenance Mode
            </v-btn>
          </div>

          <v-card id="geo" class="mb-4 config-editor__section">
            <v-expansion-panels v-model="geoPanel" flat>
              <v-expansion-panel>
                <v-expansion-panel-header class="config-editor_expansion-header px-4">
                  <div class="d-flex align-center">
                    <div class="tab-title">Geo</div>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content class="config-editor_expansion-content">
                  <v-divider></v-divider>
                  <div class="pa-4">
                    <div class="mb-4">
                      <div class="d-flex align-center mb-2">
                        <span class="body-1">Blocked Geo</span>
                      </div>
                      <div class="d-flex align-center">
                        <span class="caption grey--text">Geo</span>
                      </div>
                      <select-countries
                        :chosenCountries="formModel.settings.geo_settings.blocked_geo"
                        :countries="$options.allCountries"
                        :chip="true"
                        chip-color="#f55a4e"
                        :outlined="false"
                        :disabled="readonly"
                        @input="(items) => formModel.settings.geo_settings.blocked_geo = items.map(({ value }) => value)"
                        class="select-countries"
                      />
                    </div>

                    <div>
                      <div class="d-flex align-center mb-2">
                        <span class="body-1">Countries Available for Registration Form</span>
                      </div>
                      <div class="d-flex align-center">
                        <span class="caption grey--text">Countries</span>
                      </div>
                      <select-countries
                        :chosenCountries="formModel.settings.geo_settings.countries_registration"
                        :countries="$options.allCountries"
                        :chip="true"
                        chip-color="#4EAF50"
                        :outlined="false"
                        :disabled="readonly"
                        @input="(items) => formModel.settings.geo_settings.countries_registration = items.map(({ value }) => value)"
                        class="select-countries"
                      />
                    </div>

                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card>

          <v-card id="locales" class="mb-4 config-editor__section">
              <v-expansion-panels v-model="localesPanel" flat>
                <v-expansion-panel>
                  <v-expansion-panel-header class="config-editor_expansion-header px-4">
                    <div class="d-flex align-center">
                      <div class="tab-title">Locales</div>
                      <v-tooltip right>
                        <template #activator="{ on }">
                          <v-icon small color="grey" class="ml-1" v-on="on">mdi-information-outline</v-icon>
                        </template>
                        <span>Available locales for this configuration</span>
                      </v-tooltip>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="config-editor_expansion-content">
                    <v-divider></v-divider>
                    <div class="pa-4">
                      <div class="mb-4">
                        <div class="d-flex align-center mb-2">
                          <span class="body-1">Default Locale</span>
                        </div>
                        <div v-if="defaultLocaleChip" class="d-flex align-center" style="gap: 6px">
                          <lang-chip
                            :lang="defaultLocaleChip"
                            color="#219653"
                            colorText="white--text"
                          />
                          <v-icon small color="success">mdi-check</v-icon>
                        </div>
                        <span v-else class="caption grey--text">Not set</span>
                      </div>

                      <v-divider class="mb-4" />

                      <div>
                        <div class="d-flex align-center mb-3" >
                          <span class="body-1">Allowed Locales</span>
                        </div>
                        <div class="d-flex align-center flex-wrap" style="gap: 8px; position: relative">
                          <div
                            v-for="locale in formModel.settings.locales.list"
                            :key="locale"
                            @click="!readonly && setDefaultLocale(locale)"
                          >
                            <lang-chip
                              :lang="localeToChip(locale)"
                              :color="locale === formModel.settings.locales.default ? '#219653' : '#6FCF97'"
                              :colorText="locale === formModel.settings.locales.default ? 'white--text' : 'black--text'"
                            />
                          </div>

                          <select-lang
                            v-if="!readonly"
                            v-model="formModel.settings.locales.list"
                            :isOpen="isLocaleSelectorOpen"
                            :isRounded="false"
                            :languages="$store.state.auth.languages"
                            @open="(val) => !readonly && (isLocaleSelectorOpen = val)"
                            style="z-index: 100"
                          >
                            <template v-slot:header>
                              <v-icon>mdi-plus</v-icon>
                            </template>
                          </select-lang>
                        </div>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card>

            <v-card id="emails" class="mb-4 config-editor__section" style="z-index: 0">
              <v-expansion-panels v-model="emailsPanel" flat>
                <v-expansion-panel>
                  <v-expansion-panel-header class="config-editor_expansion-header px-4">
                    <div class="d-flex align-center">
                      <div class="tab-title">E-mails</div>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="config-editor_expansion-content">
                    <v-divider />
                    <div class="pa-4">
                      <v-row class="ma-0">
                        <v-col cols="10" md="2" class="py-2 pa-0">
                          <v-text-field
                            v-model="formModel.settings.emails.title"
                            label="Title"
                            dense
                            hide-details
                            :readonly="readonly"
                          />
                        </v-col>
                      </v-row>

                      <v-row class="ma-0 mt-4 d-flex justify-space-between">
                        <v-col
                          v-for="row in emailContactRows"
                          :key="row.target"
                          cols="10"
                          md="2"
                          class="py-3 pa-0"
                        >
                          <v-text-field
                            :value="row.email"
                            :label="row.label"
                            type="email"
                            dense
                            hide-details
                            :readonly="readonly"
                            @input="setEmailContact(row.target, $event)"
                          />
                        </v-col>
                      </v-row>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card>

            <v-card id="feature-flag" class="mt-4 config-editor__section" style="z-index: 0">
              <v-expansion-panels v-model="featureFlagsPanel" flat>
                <v-expansion-panel>
                  <v-expansion-panel-header class="config-editor_expansion-header px-4">
                    <div class="d-flex align-center">
                      <div class="tab-title">Feature flags</div>
                      <v-tooltip right>
                        <template #activator="{ on }">
                          <v-icon small color="grey" class="ml-1" v-on="on">mdi-information-outline</v-icon>
                        </template>
                        <span>Toggle feature flags for this configuration</span>
                      </v-tooltip>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="config-editor_expansion-content">
                    <v-divider />
                    <div class="pa-4">
                      <div class="d-flex flex-wrap align-center py-2" style="gap: 32px">
                        <div class="d-flex align-center">
                          <v-switch
                            v-model="formModel.settings.flags.iovation"
                            hide-details
                            color="success"
                            class="mt-0 pa-0"
                            :disabled="readonly"
                          />
                          <span class="body-2 mr-3 text-uppercase">Iovation</span>
                        </div>
                        <div class="d-flex align-center">
                          <v-switch
                            v-model="formModel.settings.flags.seo_canonical"
                            hide-details
                            color="success"
                            class="mt-0 pa-0"
                            :disabled="readonly"
                          />
                          <span class="body-2 mr-3 text-uppercase">SEO Canonical</span>
                        </div>
                        <div class="d-flex align-center">
                          <v-switch
                            v-model="formModel.settings.flags.seo_hreflang"
                            hide-details
                            color="success"
                            class="mt-0 pa-0"
                            :disabled="readonly"
                          />
                          <span class="body-2 mr-3 text-uppercase">SEO Hreflang</span>
                        </div>
                      </div>

                      <v-divider class="my-4" />

                      <div class="body-1 mb-2">Registration</div>
                      <v-radio-group
                        v-model="formModel.settings.flags.registration_type"
                        row
                        hide-details
                        class="mt-0"
                        :disabled="readonly"
                      >
                        <v-radio
                          v-for="reg in $options.registrationTypeOptions"
                          :key="reg.value"
                          :label="reg.label"
                          :value="reg.value"
                          color="success"
                          class="mr-6"
                        />
                      </v-radio-group>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card>

            <v-card id="holiday-themes" class="mt-4 config-editor__section" style="z-index: 0">
              <v-expansion-panels v-model="themesPanel" flat>
                <v-expansion-panel>
                  <v-expansion-panel-header class="config-editor_expansion-header px-4">
                    <div class="d-flex align-center">
                      <div class="tab-title">Holiday Themes</div>
                      <v-tooltip right>
                        <template #activator="{ on }">
                          <v-icon small color="grey" class="ml-1" v-on="on">mdi-information-outline</v-icon>
                        </template>
                        <span>Configure active holiday themes for this project</span>
                      </v-tooltip>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="config-editor_expansion-content">
                    <v-divider />
                    <div class="pa-4">

                      <div class="d-flex align-center mt-4 mb-5">
                        <v-select
                          v-model="formModel.settings.themes.default"
                          :items="$options.holidayThemeOptions"
                          label="Default Theme (always active)"
                          item-text="label"
                          item-value="value"
                          dense
                          hide-details
                          :disabled="readonly"
                          style="max-width: 220px"
                        />

                        <v-btn
                          v-if="!readonly"
                          text
                          color="success"
                          small
                          class="ml-3"
                          @click="addHolidayTheme"
                        >
                          <v-icon left small>mdi-plus-circle-outline</v-icon>
                          Add theme
                        </v-btn>
                      </div>

                      <div
                        v-for="row in holidayThemeRows"
                        :key="row.themeKey"
                        class="d-flex align-center mb-4"
                      >
                        <div class="mr-4" style="min-width: 180px">
                          <v-select
                            :value="row.themeKey"
                            :items="holidayThemeSelectItems(row.themeKey)"
                            label="Holiday Theme"
                            item-text="label"
                            item-value="value"
                            dense
                            hide-details
                            :disabled="readonly"
                            @input="setHolidayThemeKey(row.themeKey, $event)"
                            style="max-width: 220px"
                            class="mt-4"
                          />
                        </div>

                        <div class="mr-4">
                          <date-picker
                            v-model="row.period.start_date"
                            label="Start date"
                            :readonly="readonly"
                            style="max-width: 220px"
                          />
                        </div>

                        <div class="mr-4">
                          <date-picker
                            v-model="row.period.end_date"
                            label="End date"
                            :readonly="readonly"
                            style="max-width: 220px"
                          />
                        </div>

                        <div class="d-flex align-center mt-auto">
                          <v-tooltip v-if="isHolidayPeriodOverlapping(row.themeKey)" bottom>
                            <template #activator="{ on }">
                              <v-icon color="error" small class="mr-2" v-on="on">mdi-alert-circle-outline</v-icon>
                            </template>
                            <span>Period overlaps with another theme</span>
                          </v-tooltip>

                          <v-btn
                            v-if="!readonly"
                            icon
                            color="error"
                            @click="removeHolidayTheme(row.themeKey)"
                          >
                            <v-icon small>mdi-minus-circle-outline</v-icon>
                          </v-btn>
                        </div>
                      </div>

                      <div v-if="!holidayThemeRows.length" class="caption grey--text">
                        No holiday themes configured
                      </div>

                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card>

            <v-card id="support-chat" class="mt-4 config-editor__section" style="z-index: 0">
              <v-expansion-panels v-model="chatPanel" flat>
                <v-expansion-panel>
                  <v-expansion-panel-header class="config-editor_expansion-header px-4">
                    <div class="d-flex align-center flex-wrap" style="gap:8px;">
                      <span class="tab-title">Support Chat</span>
                      <div class="d-flex" style="gap:16px;">
                        <v-tooltip bottom>
                          <template #activator="{ on, attrs }">
                            <v-icon
                              small
                              color="grey"
                              class="config-editor__panel-mode-info"
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-information
                            </v-icon>
                          </template>
                          <span>Inputs: form fields. Code: raw JSON editor.</span>
                        </v-tooltip>
                        <div
                          class="d-flex align-center"
                          style="gap: 8px"
                          @click.stop
                        >
                          <span
                            class="body-2"
                            :class="!chat.inputs ? 'success--text font-weight-medium' : 'grey--text'"
                          >Code</span>
                          <v-switch
                            v-model="chat.inputs"
                            hide-details
                            dense
                            inset
                            color="success"
                            class="mt-0 pa-0 config-editor-switch"
                            :disabled="readonly"
                          />
                          <span
                            class="body-2"
                            :class="chat.inputs ? 'success--text font-weight-medium' : 'grey--text'"
                          >Inputs</span>
                        </div>
                      </div>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="config-editor_expansion-content">
                    <v-divider />
                    <div class="pa-4">
                      <div class="d-flex justify-space-between align-center mb-3" style="gap: 12px">
                        <v-select
                          v-model="formModel.support_chat_id"
                          :items="chatItems"
                          label="Chat config"
                          item-text="text"
                          item-value="value"
                          dense
                          hide-details
                          :disabled="readonly"
                          style="max-width: 240px"
                        />

                      </div>
                      <template v-if="activeChat">
                        <template v-if="chat.inputs">
                          <v-row
                            v-for="field in ($options.supportChatFieldsMap[activeSupportChatType] || [])"
                            :key="field.key"
                            class="ma-0 mt-4 flex-wrap align-center"
                            style="gap: 16px"
                          >
                            <template v-if="field.type === 'boolean'">
                              <v-switch
                                :input-value="Boolean(getChatFieldValue(field.key, field.type))"
                                hide-details
                                color="success"
                                class="mt-0 pa-0"
                                :disabled="readonly"
                                @change="setChatFieldValue(field.key, field.type, $event)"
                              />
                              <span class="body-2 text-uppercase">{{ field.label }}</span>
                            </template>
                            <v-text-field
                              v-else
                              :value="getChatFieldValue(field.key, field.type)"
                              :label="field.label"
                              type="text"
                              class="mt-4"
                              dense
                              hide-details
                              :readonly="readonly"
                              style="max-width: 620px"
                              @input="setChatFieldValue(field.key, field.type, $event)"
                            />
                          </v-row>
                        </template>
                        <vue-json-editor
                          v-else
                          v-model="supportChatsModel"
                          :mode="jsonEditorMode"
                          :modes="[]"
                          :show-btns="false"
                          :expandedOnStart="true"
                          class="mt-4"
                        />
                      </template>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card>

            <v-card id="license" class="mt-4 config-editor__section" style="z-index: 0">
              <v-expansion-panels v-model="licensePanel" flat>
                <v-expansion-panel>
                  <v-expansion-panel-header class="config-editor_expansion-header px-4">
                    <div class="d-flex align-center flex-wrap" style="gap: 8px">
                      <span class="tab-title">License</span>
                      <div class="d-flex" style="gap: 16px">
                        <v-tooltip bottom>
                          <template #activator="{ on, attrs }">
                            <v-icon
                              small
                              color="grey"
                              class="config-editor__panel-mode-info"
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-information
                            </v-icon>
                          </template>
                          <span>Inputs: form fields. Code: raw JSON for the selected license.</span>
                        </v-tooltip>
                        <div
                          class="d-flex align-center"
                          style="gap: 8px"
                          @click.stop
                        >
                          <span
                            class="body-2"
                            :class="!license.inputs ? 'success--text font-weight-medium' : 'grey--text'"
                          >Code</span>
                          <v-switch
                            v-model="license.inputs"
                            hide-details
                            dense
                            inset
                            color="success"
                            class="mt-0 pa-0 config-editor-switch"
                            :disabled="readonly"
                          />
                          <span
                            class="body-2"
                            :class="license.inputs ? 'success--text font-weight-medium' : 'grey--text'"
                          >Inputs</span>
                        </div>
                      </div>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="config-editor_expansion-content">
                    <v-divider />
                    <div class="pa-4">
                      <v-row class="ma-0 align-center flex-wrap" style="gap: 16px">
                        <v-select
                          v-model="formModel.license_id"
                          :items="licenseSelectItems"
                          label="License type"
                          item-text="text"
                          item-value="value"
                          dense
                          hide-details
                          :disabled="readonly"
                          style="max-width: 280px"
                        />
                        <v-text-field
                          :value="activeLicense && activeLicense.key"
                          label="ID / Key"
                          dense
                          hide-details
                          style="max-width: 420px"
                          :readonly="readonly"
                          :disabled="!activeLicense"
                          @input="activeLicense && $set(activeLicense, 'key', $event)"
                        />
                        <v-switch
                          v-model="license.visible"
                          label="Visible license info"
                          hide-details
                          color="success"
                          class="mt-0"
                          :disabled="readonly"
                        />
                      </v-row>

                      <div v-if="license.visible && activeLicense">
                        <template v-if="license.inputs">
                          <v-row class="ma-0 mt-4 flex-wrap" style="gap: 16px">
                            <v-text-field
                              v-model="activeLicense.url"
                              label="URL"
                              dense
                              hide-details
                              placeholder="https://..."
                              :readonly="readonly"
                              style="max-width: 360px"
                            />
                            <v-select
                              v-model="activeLicense.target"
                              :items="$options.licenseLinkTargetOptions"
                              label="Open"
                              item-text="label"
                              item-value="value"
                              dense
                              hide-details
                              :disabled="readonly"
                              style="max-width: 160px"
                            />
                          </v-row>
                          <v-row class="ma-0 mt-4 flex-wrap align-end" style="gap: 16px">
                            <v-text-field
                              v-model="activeLicense.image.src"
                              label="Image URL"
                              dense
                              hide-details
                              placeholder="https://..."
                              :readonly="readonly"
                              style="max-width: 360px"
                            />
                            <v-select
                              v-model="activeLicense.image.type"
                              :items="$options.licenseImageTypeOptions"
                              label="Config image type"
                              item-text="label"
                              item-value="value"
                              dense
                              hide-details
                              :disabled="readonly"
                              style="max-width: 160px"
                            />
                          </v-row>
                          <div class="mt-4">
                            <div class="caption mb-1">Extra config</div>
                            <vue-json-editor
                              v-model="activeLicense.extra_config"
                              :mode="jsonEditorMode"
                              :modes="[]"
                              :show-btns="false"
                              :expandedOnStart="true"
                            />
                          </div>

                          <v-card class="mt-4 pa-4" outlined>
                            <v-row class="ma-0 pa-0 align-center flex-wrap" style="gap: 8px">
                              <span class="body-2">Localization</span>
                              <select-lang
                                v-if="!readonly"
                                v-model="licenseLocalesModel"
                                :isOpen="license.langOpen"
                                :isRounded="false"
                                :languages="$store.state.auth.languages"
                                @open="(val) => !readonly && (license.langOpen = val)"
                                style="z-index: 100"
                              >
                                <template v-slot:header>
                                  <v-icon>mdi-plus</v-icon>
                                </template>
                              </select-lang>
                            </v-row>
                            <div class="d-flex flex-wrap mt-3" style="gap: 8px">
                              <div
                                v-for="lang in licenseLocaleList"
                                :key="lang"
                                @click="!readonly && (license.loc = lang)"
                              >
                                <lang-chip
                                  :color="license.loc === lang ? '#219653' : '#6FCF97'"
                                  :colorText="license.loc === lang ? 'white--text' : 'black--text'"
                                  :lang="localeToChip(lang)"
                                />
                              </div>
                            </div>
                            <v-textarea
                              v-if="license.loc"
                              v-model="licenseContentModel"
                              class="mt-4"
                              label="Content"
                              auto-grow
                              rows="6"
                              dense
                              outlined
                              hide-details
                              :readonly="readonly"
                            />
                          </v-card>
                        </template>
                        <vue-json-editor
                          v-else
                          v-model="activeLicenseModel"
                          :mode="jsonEditorMode"
                          :modes="[]"
                          :show-btns="false"
                          :expandedOnStart="true"
                          class="mt-4"
                        />
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card>

            <v-dialog
              v-if="!readonly"
              :value="maintenanceDialog"
              @input="maintenanceDialog = false"
              width="700"
            >
              <v-card>
                <v-card-title class="text-h5 d-flex align-center justify-space-between">
                  <span>Maintenance mode</span>
                  <v-btn icon @click="maintenanceDialog = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="py-4 body-1">
                  Maintenance
                </v-card-text>
                <v-card-actions class="pa-4">
                  <v-spacer />
                  <v-btn text class="px-2" @click="maintenanceDialog = false">
                    Cancel
                  </v-btn>
                  <v-btn
                    outlined
                    :color="isMaintenanceActive ? 'error' : 'success'"
                    class="px-2"
                    @click="onMaintenanceSubmit"
                  >
                    {{ isMaintenanceActive ? 'Deactivate' : 'Activate' }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

        </div>

      </v-card>
    </v-container>


  </div>
</template>

<script>
  import { cloneDeep, defaultsDeep, get, isEqual, merge, set } from 'lodash'
  import vueJsonEditor from 'vue-json-editor'
  import SelectLang from '@/components/core/SelectLang/SelectLang.vue'
  import SelectCountries from '@/components/core/SelectCountries.vue'
  import LangChip from '@/components/core/SelectLang/LangChip.vue'
  import { allCountries, mapperIsoLangToOurLocal, localeToIsoPrefix } from '@/components/core/SelectLang/mapperIsoLocale'
  import {
    CONFIG_EDITOR_TABS,
    EMAIL_CONTACT_TARGETS,
    HOLIDAY_THEME_ENUM,
    HOLIDAY_THEME_OPTIONS,
    LICENSE_IMAGE_TYPE_OPTIONS,
    LICENSE_LINK_TARGET_OPTIONS,
    REGISTRATION_TYPE_OPTIONS,
  } from '@/modules/feature-config/constants/const'
  import DatePicker from '@/components/core/DatePicker/DatePicker.vue'

  const SUPPORT_CHAT_FIELDS = {
    livechat: [
      { key: 'api_key', label: 'API KEY', type: 'string' },
      { key: 'integration_name', label: 'INTEGRATION NAME', type: 'string' },
      { key: 'product_name', label: 'PRODUCT NAME', type: 'string' },
      { key: 'chat_between_groups', label: 'CHAT BETWEEN GROUPS', type: 'boolean' },
      { key: 'update_session_variables.origin', label: 'Origin', type: 'string' },
    ],
    comm100: [
      { key: 'api_key', label: 'API KEY', type: 'string' },
      { key: 'button_id', label: 'BUTTON ID', type: 'string' },
      { key: 'code_plan', label: 'CODE PLAN', type: 'string' },
      { key: 'url', label: 'URL', type: 'string' },
      { key: 'fallback_url', label: 'FALLBACK URL', type: 'string' },
    ],
    'live-agent': [
      { key: 'api_key', label: 'API KEY', type: 'string' },
      { key: 'button_id', label: 'BUTTON ID', type: 'string' },
      { key: 'src', label: 'SRC', type: 'string' },
    ],
    zendesk: [
      { key: 'api_key', label: 'API KEY', type: 'string' },
      { key: 'zendesk_settings.web_widget.chat.suppress', label: 'CHAT SUPPRESS', type: 'boolean' },
    ],
    tawk: [
      { key: 'api_key', label: 'API KEY', type: 'string' },
    ],
  }

  const CHAT_FIELD_DEFAULTS = {
    string: '',
    boolean: false,
  }

  const SKELETON_CONFIG = {
    settings_id: null,
    entity_id: null,
    main_domain_id: null,
    mirror_id: null,
    license_id: null,
    support_chat_id: null,
    settings: {
      geo_settings: {
        blocked_geo: [],
        countries_registration: [],
        target_geo: null,
      },
      flags: {
        iovation: false,
        seo_canonical: false,
        seo_hreflang: false,
        registration_type: '',
      },
      emails: {
        title: 'Contact us',
        contacts: [],
      },
      themes: {
        default: HOLIDAY_THEME_ENUM.DEFAULT,
        holidays: {},
      },
      maintenance: false,
      support_chat_item: {
        api_key: '',
        integration_name: '',
        product_name: '',
        chat_between_groups: false,
        button_id: '',
        code_plan: '',
        url: '',
        fallback_url: '',
        src: '',
        update_session_variables: {
          origin: '',
        },
        zendesk_settings: {
          web_widget: {
            chat: {
              suppress: false,
            },
          },
        },
        extra_config: {},
      },
      support_chats: {},
      license_item: {
        key: '',
        extra_config: {},
        localization: {},
        url: '',
        target: '_blank',
        image: { src: '', type: 'basic-small' },
      },
      licenses: {},
      locales: {
        list: [],
        default: null,
      },
    },
  }

  export default {
    name: 'ConfigEditor',
    configEditorTabs: CONFIG_EDITOR_TABS,
    supportChatFieldsMap: SUPPORT_CHAT_FIELDS,
    registrationTypeOptions: REGISTRATION_TYPE_OPTIONS,
    holidayThemeOptions: HOLIDAY_THEME_OPTIONS,
    licenseLinkTargetOptions: LICENSE_LINK_TARGET_OPTIONS,
    licenseImageTypeOptions: LICENSE_IMAGE_TYPE_OPTIONS,
    allCountries,

    components: {
      SelectLang,
      SelectCountries,
      LangChip,
      DatePicker,
      vueJsonEditor,
    },

    props: {
      project: {
        type: Number,
        default: null,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      mode: {
        type: String,
        default: 'main',
        validator: (value) => ['main', 'mirror'].includes(value),
      },
      configData: {
        type: Object,
        required: true
      },
      mainDomain: {
        type: Object,
        required: true
      },
      licenses: {
        type: Array,
        default: () => [],
      },
      supports: {
        type: Array,
        default: () => [],
      },
      readonly: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['update:hasChanges', 'maintenance-submit'],

    data: () => ({
      innerTab: 'geo',
      localesPanel: 0,
      geoPanel: 0,
      featureFlagsPanel: 0,
      emailsPanel: 0,
      themesPanel: 0,
      chatPanel: 0,
      licensePanel: 0,
      chat: {
        inputs: true,
      },
      license: {
        visible: true,
        inputs: true,
        loc: null,
        langOpen: false,
      },
      isLocaleSelectorOpen: false,
      maintenanceDialog: false,
      formModel: cloneDeep(SKELETON_CONFIG),
      initModel: cloneDeep(SKELETON_CONFIG),
    }),

    computed: {
      hasChanges() {
        return !isEqual(this.formModel, this.initModel)
      },
      isMaintenanceActive() {
        return !!this.formModel?.settings?.maintenance
      },
      jsonEditorMode() {
        return this.readonly ? 'view' : 'code'
      },

      defaultLocaleChip() {
        const locale = this.formModel.settings.locales.default
        return locale ? this.localeToChip(locale) : null
      },
      emailContactRows() {
        const contacts = this.formModel.settings?.emails?.contacts || []

        return EMAIL_CONTACT_TARGETS.map(({ target, label }) => {
          const found = contacts.find(c => c?.target === target)

          return {
            target,
            label,
            email: found?.email ?? '',
          }
        })
      },

      holidayThemeOrder() {
        return HOLIDAY_THEME_OPTIONS.map((o) => o.value).filter(
          (v) => v !== HOLIDAY_THEME_ENUM.DEFAULT
        )
      },

      holidayThemeRows() {
        const h = this.formModel.settings?.themes?.holidays || {}
        const order = this.holidayThemeOrder

        return Object.keys(h)
          .sort((a, b) => {
            const ia = order.indexOf(a)
            const ib = order.indexOf(b)
            if (ia === -1 && ib === -1) return a.localeCompare(b)
            if (ia === -1) return 1
            if (ib === -1) return -1
            return ia - ib
          })
          .map(themeKey => ({ themeKey, period: h[themeKey] }))
      },

      chatItems() {
        return this.supports?.map?.((s) => ({
          text: `${s.type}`,
          value: s.support_chat_id,
        })) ?? []
      },

      activeSupportChatKey() {
        return this.formModel.support_chat_id == null || this.formModel.support_chat_id === ''
          ? null
          : String(this.formModel.support_chat_id)
      },

      activeChat() {
        return this.activeSupportChatKey
          ? this.formModel.settings.support_chats?.[this.activeSupportChatKey]
          : null
      },

      /** Ключ для SUPPORT_CHAT_FIELDS: из записи чата или из списка supports, в формате как в мапе (нижний регистр, _ → -). */
      activeSupportChatType() {
        const id = this.activeSupportChatKey
        if (!id) return ''
        const t = (this.supports || []).find(
          (s) => String(s.support_chat_id) === String(id)
        )?.type
        return t ? String(t).toLowerCase().replace(/_/g, '-') : ''
      },

      supportChatsModel: {
        get() {
          return this.formModel.settings.support_chats
        },
        set(v) {
          if (!v || typeof v !== 'object') return
          this.$set(this.formModel.settings, 'support_chats', v)
        },
      },

      licenseSelectItems() {
        return (this.licenses || []).map((l) => ({
          text: `${l.type}`,
          value: l.license_id,
        }))
      },

      activeLicenseKey() {
        return this.formModel.license_id == null || this.formModel.license_id === ''
          ? null
          : this.formModel.license_id
      },

      activeLicense() {
        return this.activeLicenseKey
          ? this.formModel.settings.licenses[this.activeLicenseKey]
          : null
      },

      activeLicenseModel: {
        get() {
          return this.activeLicense
        },
        set(v) {
          if (!this.activeLicenseKey || !v || typeof v !== 'object') return
          this.$set(this.formModel.settings.licenses, this.activeLicenseKey, v)
        },
      },

      licenseLocaleList() {
        return Object.keys(this.activeLicense?.localization || {}).sort()
      },

      licenseLocalesModel: {
        get() {
          return this.licenseLocaleList
        },
        set(next) {
          if (!this.activeLicense) return
          const prev = this.licenseLocaleList
          next.forEach((locale) => {
            if (!this.activeLicense.localization[locale]) {
              this.$set(this.activeLicense.localization, locale, { content: '' })
            }
          })
          prev
            .filter((locale) => !next.includes(locale))
            .forEach((locale) => this.$delete(this.activeLicense.localization, locale))
          if (!next.includes(this.license.loc)) {
            this.license.loc = next[0] || null
          }
        },
      },

      licenseContentModel: {
        get() {
          return this.activeLicense?.localization?.[this.license.loc]?.content || ''
        },
        set(v) {
          if (!this.activeLicense || !this.license.loc) return
          this.$set(this.activeLicense.localization, this.license.loc, { content: v })
        },
      },

    },

    watch: {
      configData: {
        immediate: true,
        handler(val) {
          this.initFromConfig(val)
          this.innerTab = 'geo'
        },
      },

      hasChanges(val) {
        this.$emit('update:hasChanges', val)
      },

      'formModel.license_id': {
        immediate: true,
        handler() {
          this.normalize(this.formModel.settings.licenses || {}, this.activeLicenseKey, SKELETON_CONFIG.settings.license_item)
        },
      },

      licenseLocaleList: {
        immediate: true,
        handler(list = []) {
          if (!list.includes(this.license.loc)) {
            this.license.loc = list[0] || null
          }
        },
      },

      'formModel.support_chat_id': {
        immediate: true,
        handler() {
          this.normalize(
            this.formModel.settings.support_chats || {},
            this.activeSupportChatKey,
            SKELETON_CONFIG.settings.support_chat_item
          )
        },
      },
      'formModel.settings.locales.list': {
        deep: true,
        handler(list = []) {
          const locales = this.formModel.settings.locales
          if (!list.length) {
            locales.default = null
            return
          }
          if (!list.includes(locales.default)) {
            locales.default = list[0]
          }
        },
      },
    },

    methods: {
      scrollToSection(value) {
        const open = {
          geo: () => { this.geoPanel = 0 },
          locales: () => { this.localesPanel = 0 },
          emails: () => { this.emailsPanel = 0 },
          'feature-flag': () => { this.featureFlagsPanel = 0 },
          'holiday-themes': () => { this.themesPanel = 0 },
          'support-chat': () => { this.chatPanel = 0 },
          license: () => { this.licensePanel = 0 },
        }
        open[value]?.()

        this.$nextTick(() => {
          const el = document.getElementById(value)
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      },

      initFromConfig(rawConfig) {
        const baseModel = cloneDeep(SKELETON_CONFIG)
        baseModel.entity_id = this.project ?? null

        this.formModel = rawConfig
          ? merge(baseModel, rawConfig)
          : baseModel
        this.initModel = cloneDeep(this.formModel)
      },

      normalize(map, key, skeleton) {
        key && this.$set(map, key, merge(cloneDeep(skeleton), map[key] || {}))
      },

      getChatFieldValue(path, type) {
        return get(this.activeChat, path, CHAT_FIELD_DEFAULTS[type])
      },

      setChatFieldValue(path, type, value) {
        set(this.activeChat, path, type === 'boolean' ? Boolean(value) : String(value ?? ''))
      },

      holidayThemeSelectItems(currentKey) {
        const used = Object.keys(this.formModel.settings.themes.holidays || {})
        return HOLIDAY_THEME_OPTIONS.filter(({ value }) =>
          value !== HOLIDAY_THEME_ENUM.DEFAULT &&
          (value === currentKey || !used.includes(value))
        )
      },

      setHolidayThemeKey(oldKey, newKey) {
        if (!newKey || oldKey === newKey) return
        const h = this.formModel.settings.themes.holidays
        this.$delete(h, oldKey)
        this.$set(h, newKey, { ...h[oldKey] })
      },

      localeToChip(locale) {
        return {
          locale,
          urlFlag: mapperIsoLangToOurLocal(localeToIsoPrefix(locale)),
        }
      },

      setDefaultLocale(locale) {
        if (this.readonly) return
        this.formModel.settings.locales.default = locale
      },

      setEmailContact(target, email) {
        const emails = this.formModel.settings.emails

        if (!emails.contacts) {
          this.$set(emails, 'contacts', [])
        }

        const contacts = emails.contacts
        const existing = contacts.find(c => c.target === target)

        if(existing) {
          this.$set(existing, 'email', email)
        } else {
          contacts.push({ target, email })
        }
      },
      onMaintenanceSubmit() {
        if (this.readonly) return
        const nextValue = !this.isMaintenanceActive
        this.$emit('maintenance-submit', {feature_config: this.initModel, maintenance: nextValue})
        this.maintenanceDialog = false
      },

      addHolidayTheme() {
        if (this.readonly) return
        const currentHolidays = this.formModel.settings.themes.holidays || {}
        const newHoliday = HOLIDAY_THEME_OPTIONS.find(
          ({ value }) => value !== HOLIDAY_THEME_ENUM.DEFAULT && !(value in currentHolidays)
        )
        if (newHoliday) {
          this.$set(currentHolidays, newHoliday.value, { start_date: null, end_date: null })
        }
      },

      removeHolidayTheme(themeKey) {
        if (this.readonly) return
        this.$delete(
          this.formModel.settings.themes.holidays,
          themeKey
        )
      },

      isHolidayPeriodOverlapping(themeKey) {
        const h = this.formModel.settings.themes.holidays
        const current = h[themeKey]

        if (!current?.start_date || !current?.end_date) return false

        return Object.entries(h).some(([k, o]) =>
          k !== themeKey &&
          o?.start_date &&
          o?.end_date &&
          current.start_date <= o.end_date &&
          current.end_date >= o.start_date
        )
      },

      buildPayload() {
        const changedSettings = {}
        const current = this.formModel.settings || {}
        const initial = this.initModel.settings || {}

        Object.keys(current).forEach((key) => {
          if (!isEqual(current[key], initial[key])) {
            changedSettings[key] = cloneDeep(current[key])
          }
        })

        const payload = {
          settings_id: this.formModel.settings_id,
          mirror_id: this.formModel.mirror_id,
          settings: changedSettings,
        }

        if (!isEqual(this.formModel.license_id, this.initModel.license_id)) {
          payload.license_id = this.formModel.license_id
        }

        if (!isEqual(this.formModel.support_chat_id, this.initModel.support_chat_id)) {
          payload.support_chat_id = this.formModel.support_chat_id
        }

        return payload
      },

      onCancel() {
        const cloned = cloneDeep(this.initModel)
        this.$set(this, 'formModel', cloned)
      },
    },
  }
</script>

<style lang="scss" scoped>
  .config-editor {
    .select-countries {
      ::v-deep .custom-select {
        margin-left: 0;
        max-width: 548px;
      }
    }

    &__tabs {

      &-sticky {
        position: sticky;
        top: 0;
        z-index: 5;
        margin-bottom: 16px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
      }

      ::v-deep .v-tab {
        font-size: 12px;
        letter-spacing: 0.04em;
      }
    }

    &__section {
      scroll-margin-top: 52px;
    }

    .tab-title {
      font-size: 18px;
    }

    &-switch {
      flex-shrink: 0;

      ::v-deep .v-input--selection-controls__input {
        margin-right: 0;
      }
    }

    &__panel-mode-info {
      cursor: help;
    }

    &_expansion {
      &-header {
        min-height: 56px !important;
      }
      &-content {
        ::v-deep .v-expansion-panel-content__wrap {
          padding: 0;
        }
      }
    }
  }
</style>
